import { Component, effect, input } from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { SfoUiJSONSchema7 } from '../../../metadata.model';

type NameMode = 'person' | 'literal';

@Component({
  selector: 'sfo-name-input',
  templateUrl: './name-input.component.html',
  styleUrls: ['./name-input.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatIconButton,
    MatIcon,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatTooltip,
    DragDropModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class NameInputComponent {
  schema = input.required<SfoUiJSONSchema7>();
  aFormControl = input.required<FormArray>();
  defaultValue = input<unknown>(undefined);

  constructor() {
    effect(() => {
      this.normalizeAllControls();
    });
  }

  private normalizeAllControls(): void {
    this.aFormControl().controls.forEach((control, index) => {
      this.ensureControlsExist(control as FormGroup);
    });
  }

  private ensureControlsExist(control: FormGroup): void {
    try {
      if (!control || !(control instanceof FormGroup)) {
        return;
      }
      if (!control.get('family')) {
        control.addControl('family', new FormControl(''));
      }
      if (!control.get('given')) {
        control.addControl('given', new FormControl(''));
      }
      if (!control.get('literal')) {
        control.addControl('literal', new FormControl(null));
      }
    } catch (error) {
      console.error('Error ensuring controls exist:', error);
    }
  }

  addName(): void {
    const newName = new FormGroup({
      family: new FormControl(''),
      given: new FormControl(''),
      literal: new FormControl(null), // null = inactive, person mode is default
    });
    this.aFormControl().push(newName);
  }

  dropName(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.moveName(event.previousIndex, event.currentIndex);
  }

  moveName(fromIndex: number, toIndex: number): void {
    const formArray = this.aFormControl();
    const length = formArray.length;
    if (fromIndex < 0 || fromIndex >= length || toIndex < 0 || toIndex >= length) {
      return;
    }

    const control = formArray.at(fromIndex);
    formArray.removeAt(fromIndex);
    formArray.insert(toIndex, control);
    formArray.markAsDirty();
    formArray.updateValueAndValidity({ emitEvent: true });
  }

  moveNameUp(index: number): void {
    this.moveName(index, index - 1);
  }

  moveNameDown(index: number): void {
    this.moveName(index, index + 1);
  }

  removeName(index: number): void {
    this.aFormControl().removeAt(index);
    this.aFormControl().markAsDirty();
    this.aFormControl().updateValueAndValidity();
  }

  switchMode(index: number, mode: NameMode): void {
    const current = this.getNameControl(index);

    if (mode === 'person') {
      const literal = (current.get('literal')?.value as string | null) || '';

      current.patchValue({
        family: literal.trim(),
        given: '',
        literal: null,
      });
    } else {
      const family = ((current.get('family')?.value as string | null) || '').trim();
      const given = ((current.get('given')?.value as string | null) || '').trim();

      let combined = '';
      if (family && given) {
        combined = `${family}, ${given}`;
      } else if (family) {
        combined = family;
      } else if (given) {
        combined = given;
      }

      current.patchValue({
        family: null,
        given: null,
        literal: combined,
      });
    }

    current.markAsDirty();
  }

  getNameControl(index: number): FormGroup {
    return this.aFormControl().at(index) as FormGroup;
  }

  /**
   * Get the current mode of the name input at the specified index. Returns 'person' if either family or given name is set, 'literal' if literal is set, defaults to 'person'.
   * @param index
   * @returns {NameMode}
   */
  getMode(index: number): NameMode {
    const control = this.getNameControl(index);

    const literalControl = control.get('literal');
    const familyControl = control.get('family');
    const givenControl = control.get('given');

    if (familyControl?.value !== null || givenControl?.value !== null) {
      return 'person';
    }

    if (literalControl?.value !== null) {
      return 'literal';
    }

    return 'person';
  }
}
